// src/screens/VendorSettingsScreen.tsx
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
} from "react";
import { supabase } from "../lib/supabase";
import { useQueryClient } from "@tanstack/react-query";
import {
  View,
  Text,
  StyleSheet,
  FlatList,
  Button,
  Dimensions,
  Modal,
  ActivityIndicator,
  Linking,
  Alert,
  TouchableOpacity,
  Platform,
} from "react-native";
import PaymentConnectCard from "./../src/components/PaymentConnectCard";
import { SafeAreaView } from "react-native-safe-area-context";

// Conditionally import WebView only for native
let WebView: any = null;
if (Platform.OS !== 'web') {
  WebView = require('react-native-webview').WebView;
}
import { Feather } from "@expo/vector-icons";

import { StorefrontCustomization } from "../components/StorefrontCustomization";
import { AddressSettings } from "../components/AddressSettings";
import { StoreHoursSettings } from "../components/vendors/StoreHoursSettings";
import { OrderSettingsSection } from "../components/vendors/OrderSettingsSection";
import { LoadingState } from "../components/ui/LoadingState";
import { useVendorProfile } from "../hooks/useVendorProfile";
import { setupExpressAccount, getExpressLoginLink } from "../services/paymentService";
import { spacing, typography } from "../theme";
import { useColors, ThemeColors } from "../hooks/useColors";
import { useNavigation, useFocusEffect } from "@react-navigation/native";
import { usePostHog } from "../hooks/usePostHog";
import { trackEvent } from "../services/posthog";
import { Section } from "../types/vendor";

export default function VendorSettingsScreen() {
  const colors = useColors();
  const navigation = useNavigation();
  const posthog = usePostHog();
  const didSyncRef = useRef(false);
  const [lastCheckedAt, setLastCheckedAt] = useState<number | null>(null);
  const { data: vendor, isLoading, refetch } = useVendorProfile();
  const queryClient = useQueryClient();
  const [onboardingUrl, setOnboardingUrl] = useState<string | null>(null);
  const [stripeModalMode, setStripeModalMode] = useState<'onboarding' | 'manage'>('onboarding');
  const [isStripeLoading, setIsStripeLoading] = useState(false);
  const [isCheckingStatus, setIsCheckingStatus] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [chargesEnabled, setChargesEnabled] = useState<boolean>(
    vendor?.stripe_account_status === "active"
  );
  // Track when Stripe window is open on web to prevent closing modal
  const [isStripeWindowOpen, setIsStripeWindowOpen] = useState(false);

  // whenever vendor changes, reset our local badge
  useEffect(() => {
    setChargesEnabled(vendor?.stripe_account_status === "active");
  }, [vendor]);

  const checkStripeStatus = useCallback(async () => {
    if (!vendor?.id) return;

    // optional: simple cooldown (e.g. 30s)
    const now = Date.now();
    if (lastCheckedAt && now - lastCheckedAt < 30_000) return;
    setLastCheckedAt(now);

    setIsCheckingStatus(true);
    try {
      const { data: session } = await supabase.auth.getSession();
      const token = session?.session?.access_token;
      if (!token) throw new Error("No session token");

      const { supabaseUrl } = supabase as any;
      const resp = await fetch(
        `${supabaseUrl}/functions/v1/syncStripeAccount`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ vendorId: vendor.id }),
        }
      );

      const json = await resp.json().catch(() => null);
      console.log("syncStripeAccount ->", resp.status, json);
      if (!resp.ok) throw new Error(json?.error || `HTTP ${resp.status}`);

      // If the server says nothing changed, don't refetch
      if (!json.updated) {
        setChargesEnabled(json?.status === "active");
        return;
      }

      // Only refetch when row was actually updated
      await refetch();
      
      // Invalidate vendor-check query used by SellScreen/ProductListingScreen
      // This ensures the updated stripe_account_status is reflected immediately
      queryClient.invalidateQueries({ queryKey: ["vendor-check"] });
    } catch (e) {
      console.error("Stripe status sync failed:", e);
    } finally {
      setIsCheckingStatus(false);
    }
  }, [vendor?.id, lastCheckedAt, refetch, queryClient]);

  useEffect(() => {
    if (!vendor?.id) return;
    const channel = supabase
      .channel(`vendor:${vendor.id}`)
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "vendors",
          filter: `id=eq.${vendor.id}`,
        },
        (payload) => {
          const row: any = payload.new;
          if (row?.stripe_account_status)
            setChargesEnabled(row.stripe_account_status === "active");
        }
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [vendor?.id]);

  useEffect(() => {
    if (!vendor?.stripe_accountid) return;
    if (didSyncRef.current) return; // already synced once for this account
    didSyncRef.current = true;
    checkStripeStatus();
  }, [vendor?.stripe_accountid, checkStripeStatus]);

  // On web: When component mounts and vendor has Stripe account with pending status, check status
  // This handles the case where user returns from Stripe onboarding
  useEffect(() => {
    if (Platform.OS !== 'web' || !vendor?.id || !vendor?.stripe_accountid) return;
    // Only check if status is pending or not yet active (user might have just completed onboarding)
    if (vendor.stripe_account_status === 'active') return;
    
    // Check status after a short delay to ensure component is fully mounted
    const timer = setTimeout(async () => {
      await checkStripeStatus();
      queryClient.invalidateQueries({ queryKey: ["vendor-check"] });
      await refetch();
      // If status is now active, show success
      const { data: updatedVendor } = await supabase
        .from('vendors')
        .select('stripe_account_status')
        .eq('id', vendor.id)
        .single();
      if (updatedVendor?.stripe_account_status === 'active') {
        setShowSuccessModal(true);
      }
    }, 2000);
    
    return () => clearTimeout(timer);
  }, [vendor?.id, vendor?.stripe_accountid, vendor?.stripe_account_status, checkStripeStatus, queryClient, refetch]);

  // On web: Listen for tab/window visibility changes to check Stripe status when user returns
  useEffect(() => {
    if (Platform.OS !== 'web') return;
    if (!vendor?.id) return;
    
    const handleVisibilityChange = async () => {
      if (document.hidden) return;
      if (!isStripeWindowOpen && vendor.stripe_account_status !== 'pending') return;
      
      // Check Stripe status but keep stripeFlowActive flag until modal closes
      await checkStripeStatus();
      queryClient.invalidateQueries({ queryKey: ["vendor-check"] });
      const freshVendor = await refetch();
      
      if (freshVendor?.data?.stripe_account_status === 'active') {
        sessionStorage.removeItem('stripeFlowActive');
        sessionStorage.removeItem('activeTabBeforeStripe');
        setIsStripeWindowOpen(false);
        setOnboardingUrl(null);
        setShowSuccessModal(true);
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [vendor?.id, vendor?.stripe_account_status, isStripeWindowOpen, checkStripeStatus, queryClient, refetch]);

  // Clear Stripe modal state when screen loses focus (but NOT on web when Stripe window is open)
  // This prevents the modal from persisting when navigating away and coming back
  useFocusEffect(
    useCallback(() => {
      // This runs when screen gains focus
      // On web, check if Stripe was completed while we were away
      if (Platform.OS === 'web' && isStripeWindowOpen) {
        console.log('[VendorSettings] Screen gained focus with Stripe window open, checking status');
        // Reset the flag and check status
        setIsStripeWindowOpen(false);
        checkStripeStatus().then(() => {
          queryClient.invalidateQueries({ queryKey: ["vendor-check"] });
          refetch();
        });
      }
      
      return () => {
        // This runs when screen loses focus - close the modal
        // BUT on web, if Stripe window is open, don't close the modal
        if (onboardingUrl && !(Platform.OS === 'web' && isStripeWindowOpen)) {
          console.log('[VendorSettings] Screen lost focus, closing Stripe modal');
          setOnboardingUrl(null);
        }
      };
    }, [onboardingUrl, isStripeWindowOpen, checkStripeStatus, queryClient, refetch])
  );

  const onSetupPayment = useCallback(async () => {
    if (!vendor?.id) {
      console.error("No vendor ID available for Stripe setup");
      return;
    }

    setIsStripeLoading(true);
    setStripeModalMode('onboarding');
    try {
      const url = await setupExpressAccount(vendor.id);
      setOnboardingUrl(url);
    } catch (err) {
      console.error("Error initiating Stripe onboarding:", err);
    } finally {
      setIsStripeLoading(false);
    }
  }, [vendor?.id]);

  const onManage = useCallback(async () => {
    if (!vendor?.stripe_accountid) return;
    
    setIsStripeLoading(true);
    setStripeModalMode('manage');
    try {
      // Get a login link for the Express dashboard
      const loginUrl = await getExpressLoginLink(vendor.stripe_accountid);
      setOnboardingUrl(loginUrl);
    } catch (err) {
      console.error("Error getting Stripe login link:", err);
      // Fallback: try opening in browser (may not work for Express accounts)
      const dashboardUrl = `https://dashboard.stripe.com/express/${vendor.stripe_accountid}`;
      if (Platform.OS === 'web') {
        window.open(dashboardUrl, '_blank');
      } else {
        Linking.openURL(dashboardUrl);
      }
    } finally {
      setIsStripeLoading(false);
    }
  }, [vendor?.stripe_accountid]);

  // Check if vendor has saved their pickup address (required before Stripe setup)
  const hasPickupAddress = useMemo(() => {
    if (!vendor) return false;
    return !!(
      vendor.street_line1 &&
      vendor.city &&
      vendor.state &&
      vendor.zip_code &&
      vendor.latitude &&
      vendor.longitude
    );
  }, [vendor]);

  // Check if vendor has saved their contact details (required before Stripe setup)
  const hasContactDetails = useMemo(() => {
    if (!vendor) return false;
    return !!(vendor.phone_number);
  }, [vendor]);

  // Determine which requirements are missing for payment setup
  const getMissingRequirements = useMemo(() => {
    const missing: string[] = [];
    if (!hasPickupAddress) missing.push("pickup address");
    if (!hasContactDetails) missing.push("contact details");
    return missing;
  }, [hasPickupAddress, hasContactDetails]);

  const canSetupPayment = hasPickupAddress && hasContactDetails;

  const paymentComponent = useMemo(() => {
    if (!vendor) return null;

    const connected = chargesEnabled === true;
    const pending = !!vendor.stripe_accountid && !connected;

    // Build a user-friendly disabled reason message
    let disabledReason: string | undefined;
    if (!canSetupPayment && !connected) {
      if (getMissingRequirements.length === 1) {
        disabledReason = `Please save your ${getMissingRequirements[0]} first`;
      } else if (getMissingRequirements.length === 2) {
        disabledReason = `Please save your ${getMissingRequirements[0]} and ${getMissingRequirements[1]} first`;
      } else {
        disabledReason = `Please save your ${getMissingRequirements.slice(0, -1).join(", ")}, and ${getMissingRequirements[getMissingRequirements.length - 1]} first`;
      }
    }

    return (
      <PaymentConnectCard
        connected={connected}
        pending={pending}
        onConnect={onSetupPayment}
        onComplete={onSetupPayment}
        onManage={onManage}
        onRefresh={checkStripeStatus}
        isChecking={isCheckingStatus}
        disabled={!canSetupPayment && !connected}
        disabledReason={disabledReason}
      />
    );
  }, [
    vendor,
    chargesEnabled,
    onSetupPayment,
    onManage,
    checkStripeStatus,
    isCheckingStatus,
    canSetupPayment,
    getMissingRequirements,
  ]);

  const sections = useMemo<Section[]>(() => {
    if (!vendor) return [];
    return [
      {
        id: "storefront",
        title: "Storefront",
        component: <StorefrontCustomization vendor={vendor} />,
      },
      {
        id: "address",
        title: "Pickup Address",
        component: <AddressSettings vendor={vendor} onUpdate={refetch} />,
      },
      {
        id: "hours",
        title: "Store Hours",
        component: <StoreHoursSettings vendor={vendor} onUpdate={refetch} />,
      },
      {
        id: "orderSettings",
        title: "Order Settings",
        component: <OrderSettingsSection vendor={vendor} onUpdate={refetch} />,
      },
      {
        id: "payment",
        title: "Payment",
        component: paymentComponent,
      },
    ];
  }, [vendor, paymentComponent, refetch]);

  if (isLoading) return <LoadingState />;
  if (!vendor) return null;

  const styles = createStyles(colors);

  return (
    <View style={styles.container}>
      {isStripeLoading && (
        <View style={styles.loadingOverlay}>
          <ActivityIndicator size="large" color={colors.primary} />
        </View>
      )}

      <FlatList
        style={styles.container}
        data={sections}
        renderItem={({ item }) => (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>{item.title}</Text>
            {item.component}
          </View>
        )}
        keyExtractor={(item) => item.id}
        contentContainerStyle={styles.listContainer}
      />

      {/* -- Onboarding Modal -- */}
      <Modal
        visible={!!onboardingUrl}
        animationType="slide"
        onRequestClose={() => {
          if (Platform.OS === 'web') {
            sessionStorage.removeItem('stripeFlowActive');
            sessionStorage.removeItem('activeTabBeforeStripe');
          }
          setIsStripeWindowOpen(false);
          setOnboardingUrl(null);
        }}
      >
        <View style={styles.modalWrapper}>
          <SafeAreaView style={styles.safeArea} edges={["top", "bottom"]}>
            <View style={styles.modalHeader}>
              <Text style={styles.headerText}>
                {stripeModalMode === 'manage' ? 'Stripe Dashboard' : 'Stripe Onboarding'}
              </Text>
              <TouchableOpacity
                onPress={() => {
                  if (Platform.OS === 'web') {
                    sessionStorage.removeItem('stripeFlowActive');
                    sessionStorage.removeItem('activeTabBeforeStripe');
                  }
                  setIsStripeWindowOpen(false);
                  setOnboardingUrl(null);
                }}
                style={styles.closeButton}
              >
                <Feather name="x" size={24} color={colors.textLight} />
              </TouchableOpacity>
            </View>
            {onboardingUrl && Platform.OS === 'web' ? (
              // Web: Open Stripe in new window (can't use iframe due to CSP)
              <View style={styles.webOnboardingContent}>
                <Text style={styles.webOnboardingTitle}>
                  {stripeModalMode === 'manage' ? 'Stripe Dashboard' : 'Stripe Onboarding'}
                </Text>
                <Text style={styles.webOnboardingText}>
                  {stripeModalMode === 'manage' 
                    ? 'Click the button below to open your Stripe dashboard in a new window. You can manage your bank accounts, view payouts, and more.'
                    : 'Click the button below to open Stripe setup in a new window. Complete the setup there, then return here and click "Done".'
                  }
                </Text>
                <View style={styles.webButtonContainer}>
                  <TouchableOpacity
                    style={styles.webButton}
                    onPress={() => {
                      if (onboardingUrl) {
                        // Mark that Stripe window is open to prevent navigation issues
                        setIsStripeWindowOpen(true);
                        // Set global flags to prevent React Navigation from resetting on web
                        if (Platform.OS === 'web') {
                          sessionStorage.setItem('stripeFlowActive', 'true');
                          sessionStorage.setItem('activeTabBeforeStripe', 'Sell');
                        }
                        window.open(onboardingUrl, '_blank', 'noopener,noreferrer');
                      }
                    }}
                  >
                    <Text style={styles.webButtonText}>Open Stripe Setup</Text>
                  </TouchableOpacity>
                  <View style={{ height: 12 }} />
                  <TouchableOpacity
                    style={styles.webButton}
                    onPress={async (e) => {
                      e?.preventDefault?.();
                      e?.stopPropagation?.();
                      // Clear the Stripe flow flags
                      if (Platform.OS === 'web') {
                        sessionStorage.removeItem('stripeFlowActive');
                        sessionStorage.removeItem('activeTabBeforeStripe');
                      }
                      setIsStripeWindowOpen(false);
                      setOnboardingUrl(null);
                      await checkStripeStatus();
                      queryClient.invalidateQueries({ queryKey: ["vendor-check"] });
                      if (vendor?.stripe_account_status === 'active') {
                        setShowSuccessModal(true);
                      }
                    }}
                  >
                    <Text style={styles.webButtonText}>Done - Check Status</Text>
                  </TouchableOpacity>
                </View>
              </View>
          ) : onboardingUrl && WebView ? (
            <WebView
              source={{ uri: onboardingUrl }}
              style={styles.webview}
              startInLoadingState
              onNavigationStateChange={async ({ url, canGoBack, canGoForward, loading, title }) => {
                console.log('WebView navigation:', { url, loading, title });
                
                // detect your deep link for successful completion
                if (url.startsWith("com.jadetrade://onboard/complete")) {
                  setOnboardingUrl(null);
                  setShowSuccessModal(true);
                  await refetch();
                  await checkStripeStatus();
                  // Track vendor setup completion
                  try {
                    trackEvent(
                      "vendor_setup_completed",
                      {
                        vendor_id: vendor?.id,
                        stripe_status: "active",
                      },
                      posthog || undefined
                    );
                  } catch (analyticsError) {
                    console.warn(
                      "[VendorSettings] Failed to track vendor_setup_completed:",
                      analyticsError
                    );
                  }
                  // Invalidate vendor-check query to update SellScreen/ProductListingScreen
                  queryClient.invalidateQueries({ queryKey: ["vendor-check"] });
                  return;
                }
                
                // Detect success indicators - Stripe usually redirects to success/completion pages
                const successIndicators = [
                  'success',
                  'complete',
                  'onboarding/complete',
                  'account/complete',
                  'verification/complete'
                ];
                
                const isSuccessPage = successIndicators.some(indicator => 
                  url.toLowerCase().includes(indicator)
                );
                
                // Detect error indicators
                const errorIndicators = [
                  'NSURLErrorDomain',
                  'Error Code: -1003',
                  'could not be found',
                  'error loading page',
                  'server error'
                ];
                
                const isErrorPage = errorIndicators.some(indicator => 
                  url.toLowerCase().includes(indicator.toLowerCase()) || 
                  (title && title.toLowerCase().includes(indicator.toLowerCase()))
                );
                
                // If we detect an error page but the onboarding might have been successful, close and check status
                if (isErrorPage && !loading) {
                  console.log('Detected error page, checking Stripe status...');
                  setOnboardingUrl(null);
                  
                  // Give Stripe a moment to process and then check status
                  setTimeout(async () => {
                    await checkStripeStatus();
                    // Invalidate vendor-check query regardless - let fresh data determine the UI
                    queryClient.invalidateQueries({ queryKey: ["vendor-check"] });
                    if (vendor?.stripe_account_status === 'active') {
                      setShowSuccessModal(true);
                      try {
                        trackEvent(
                          "vendor_setup_completed",
                          {
                            vendor_id: vendor?.id,
                            stripe_status: "active",
                          },
                          posthog || undefined
                        );
                      } catch (analyticsError) {
                        console.warn(
                          "[VendorSettings] Failed to track vendor_setup_completed (error recovery):",
                          analyticsError
                        );
                      }
                    }
                  }, 2000);
                  return;
                }
                
                // If we detect a success page
                if (isSuccessPage && !loading) {
                  console.log('Detected success page');
                  setOnboardingUrl(null);
                  setShowSuccessModal(true);
                  await refetch();
                  await checkStripeStatus();
                  // Invalidate vendor-check query to update SellScreen/ProductListingScreen
                  queryClient.invalidateQueries({ queryKey: ["vendor-check"] });
                  try {
                    trackEvent(
                      "vendor_setup_completed",
                      {
                        vendor_id: vendor?.id,
                        stripe_status: "active",
                      },
                      posthog || undefined
                    );
                  } catch (analyticsError) {
                    console.warn(
                      "[VendorSettings] Failed to track vendor_setup_completed (success page):",
                      analyticsError
                    );
                  }
                  return;
                }
              }}
            />
          ) : null}
        </SafeAreaView>
        </View>
      </Modal>

      {/* Success Modal */}
      <Modal
        visible={showSuccessModal}
        transparent
        animationType="fade"
        onRequestClose={() => setShowSuccessModal(false)}
      >
        <View style={styles.successOverlay}>
          <View style={styles.successModal}>
            <View style={styles.successIconContainer}>
              <Feather name="check-circle" size={64} color={colors.success} />
            </View>
            <Text style={styles.successTitle}>Stripe Setup Complete!</Text>
            <Text style={styles.successMessage}>
              Your Stripe account has been successfully connected. You can now start selling products and receiving payments.
            </Text>
            <TouchableOpacity
              style={styles.successButton}
              onPress={() => {
                setShowSuccessModal(false);
                // Navigate back to the Sell screen
                navigation.goBack();
              }}
            >
              <Text style={styles.successButtonText}>Continue</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>
    </View>
  );
}

const { height } = Dimensions.get("window");

const PHONE_CONTAINER_MAX_WIDTH = 430;

const createStyles = (colors: ThemeColors) =>
  StyleSheet.create({
    container: { flex: 1, backgroundColor: colors.background },
    safeArea: { flex: 1, backgroundColor: colors.background },
    modalWrapper: {
      flex: 1,
      ...(Platform.OS === 'web' ? {
        maxWidth: PHONE_CONTAINER_MAX_WIDTH,
        alignSelf: 'center',
        width: '100%',
      } : {}),
    },
    listContainer: { paddingBottom: spacing.xl },
    section: {
      paddingVertical: spacing.lg,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    sectionTitle: {
      ...typography.title,
      fontSize: 20,
      fontWeight: "600",
      marginBottom: spacing.md,
      marginHorizontal: spacing.lg,
      color: colors.text,
    },

    // Payment row
    paymentRow: {
      flexDirection: "row",
      alignItems: "center",
      gap: spacing.md,
      flexWrap: "wrap",
      marginHorizontal: spacing.lg,
    },
    statusBadgePending: {
      backgroundColor: colors.primary,
      color: colors.text,
      paddingVertical: 4,
      paddingHorizontal: 8,
      borderRadius: 4,
      ...typography.body,
    },
    statusBadgeActive: {
      backgroundColor: colors.success,
      color: colors.success,
      paddingVertical: 4,
      paddingHorizontal: 8,
      borderRadius: 4,
      ...typography.body,
    },
    linkText: {
      color: colors.primary,
      textDecorationLine: "underline",
      ...typography.body,
    },

    // Onboarding Modal styles
    headerText: {
      fontSize: 24,
      fontWeight: "700",
      color: colors.text,
    },
    modalHeader: {
      paddingHorizontal: spacing.md,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
      paddingTop: Platform.OS === 'web' ? spacing.md : 40,
      paddingBottom: spacing.sm,
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
    },
    closeButton: {
      padding: spacing.xs,
    },
    webOnboardingContent: {
      flex: 1,
      padding: spacing.xl,
      alignItems: 'center',
      justifyContent: 'center',
    },
    webOnboardingTitle: {
      color: colors.text,
      fontSize: 20,
      fontWeight: '600',
      marginBottom: spacing.md,
      textAlign: 'center',
    },
    webOnboardingText: {
      color: colors.textLight,
      textAlign: 'center',
      marginBottom: spacing.xl,
      paddingHorizontal: spacing.md,
      lineHeight: 22,
    },
    webButtonContainer: {
      width: '100%',
      maxWidth: 300,
    },
    webButton: {
      backgroundColor: colors.primary,
      paddingVertical: spacing.md,
      paddingHorizontal: spacing.lg,
      borderRadius: spacing.sm,
      alignItems: 'center',
      justifyContent: 'center',
    },
    webButtonText: {
      color: colors.white || '#FFFFFF',
      fontSize: 16,
      fontWeight: '600',
    },
    webview: { flex: 1, height: height - 64 },
    loadingOverlay: {
      ...StyleSheet.absoluteFillObject,
      alignItems: "center",
      justifyContent: "center",
      zIndex: 1000,
      backgroundColor: "rgba(255, 255, 255, 0.8)",
    },

    // Success Modal styles
    successOverlay: {
      flex: 1,
      backgroundColor: "rgba(0, 0, 0, 0.5)",
      justifyContent: "center",
      alignItems: "center",
      padding: spacing.lg,
    },
    successModal: {
      backgroundColor: colors.surface,
      borderRadius: spacing.lg,
      padding: spacing.xl,
      alignItems: "center",
      maxWidth: 350,
      width: "100%",
      shadowColor: colors.shadow,
      shadowOffset: { width: 0, height: 4 },
      shadowOpacity: 0.3,
      shadowRadius: 8,
      elevation: 8,
    },
    successIconContainer: {
      marginBottom: spacing.lg,
    },
    successTitle: {
      ...typography.title,
      fontSize: 24,
      fontWeight: "700",
      color: colors.text,
      textAlign: "center",
      marginBottom: spacing.md,
    },
    successMessage: {
      ...typography.body,
      color: colors.textSecondary,
      textAlign: "center",
      lineHeight: 22,
      marginBottom: spacing.xl,
    },
    successButton: {
      backgroundColor: colors.primary,
      paddingVertical: spacing.md,
      paddingHorizontal: spacing.xl,
      borderRadius: spacing.sm,
      width: "100%",
    },
    successButtonText: {
      ...typography.subtitle,
      color: colors.white,
      textAlign: "center",
      fontWeight: "600",
    },
  });
